import { Sangha, UserProfile, SanghaMembership } from "@app/shared/types";
import { Button, Typography, alpha } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { analyticsTrack } from "app/analytics/track";
import { externalImageSrc } from "app/images";
import TeacherDialog from "features/community/TeacherDialog";
import { CSDialog } from "components/CSDialog";
import { FadeCarousel } from "components/FadeCarousel";
import { MENU_SHADOW_HEIGHT } from "features/navigation/TopBarMenu";
import { useState } from "react";
import UserProfileHighlight from "../profile/UserProfileHighlight";
import MemberDialog from "../sangha/MemberDialog";

const useStyles = makeStyles((theme) => ({
    root: {
        display: "inline-block",
    },
    modalTitle: {
        marginTop: theme.spacing(6),

        textAlign: "center",
    },
    button: {
        width: "max-content",
        [theme.breakpoints.down("sm")]: {
            width: "100%",
        },
    },
    bottomBar: {
        position: "absolute",
        bottom: "0px",
        left: "0px",
        width: "100vw",
        boxShadow: `${MENU_SHADOW_HEIGHT}px 0 ${MENU_SHADOW_HEIGHT}px ${alpha(
            theme.palette.grey900,
            0.1,
        )}`,

        backgroundColor: theme.palette.coral100,
    },
    bottomBarCol1: {
        display: "inline-block",
        width: "50%",
        textAlign: "right",
    },
    bottomBarCol2: {
        display: "inline-block",
        width: "50%",
    },
    fadeCarousel: {
        height: 32,
        width: 32,
        borderRadius: theme.borderRadius.circular,
        marginRight: theme.spacing(2),
    },
    card: {
        backgroundColor: theme.palette.background.contrast,
        color: theme.palette.grey700,
    },
    websiteLink: {
        color: theme.palette.grey700,
    },
    teacherLabel: {
        color: theme.palette.coral100,
    },
    hideElement: {
        display: "none",
    },
}));

export const SpotsAvailableMemberList = (props: {
    sangha: Sangha;
    classes?: Partial<ReturnType<typeof useStyles>>;
}) => {
    const { sangha } = props;

    const classes = useStyles(props);
    const [teacherDialogOpen, setTeacherDialogOpen] = useState(false);
    const [dialogMember, setDialogMember] = useState<UserProfile | null>(null);

    return (
        <>
            <div className={classes.modalTitle}>
                <Typography variant="h2">The people you’ll bond with</Typography>
            </div>

            <UserProfileHighlight
                profile={{
                    ...sangha.teacher,
                    nickName: sangha.teacher?.name,
                    bio: sangha.teacher?.background,
                    isTeacher: true,
                }}
                onClick={() => {
                    setTeacherDialogOpen(true);
                    analyticsTrack(`member.spotsAvailable.teacher_profile.opened`, {
                        sanghaId: sangha.id,
                        teacherName: sangha.teacher?.name,
                    });
                }}
                classes={{
                    card: classes.card,
                    websiteLink: classes.websiteLink,
                    teacherLabel: classes.teacherLabel,
                }}
            />
            <TeacherDialog
                open={teacherDialogOpen}
                onClose={() => setTeacherDialogOpen(false)}
                teacher={sangha.teacher}
            />
            {sangha.memberships?.map((membership: SanghaMembership, i: number) => (
                <UserProfileHighlight
                    key={membership?.user?.profile.id}
                    profile={membership?.user?.profile}
                    onClick={() => {
                        setDialogMember(membership?.user?.profile);
                        analyticsTrack(`member.spotsAvailable.member_profile.opened`, {
                            sangha: sangha.id,
                            clickedUser: membership?.user?.profile.id,
                        });
                    }}
                    classes={{
                        card: classes.card,
                        websiteLink: classes.websiteLink,
                        teacherLabel: classes.teacherLabel,
                    }}
                />
            ))}
            {dialogMember && (
                <MemberDialog
                    member={dialogMember}
                    open={dialogMember !== null}
                    onClose={() => setDialogMember(null)}
                />
            )}
        </>
    );
};

export const SpotsAvailableButton = (props: {
    sangha: Sangha;
    classes?: Partial<ReturnType<typeof useStyles>>;
}) => {
    const { sangha } = props;

    const classes = useStyles(props);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const pictures: string[] =
        sangha?.memberships
            ?.filter((membership) => membership?.user?.profile?.picture)
            .map((membership) => externalImageSrc(membership.user.profile.picture as string, 50)) ??
        [];
    return (
        <>
            <Button
                variant="secondary"
                size="small"
                className={classes.button}
                onClick={() => {
                    setIsModalOpen(true);
                    analyticsTrack(`member.spotsAvailable.button.clicked`, {
                        sanghaId: sangha.id,
                    });
                }}
                data-testid="spotsAvailableButton"
            >
                <FadeCarousel
                    images={pictures}
                    interval={3000}
                    classes={{
                        container: classes.fadeCarousel,
                        image: classes.fadeCarousel,
                    }}
                />
                {sangha?.openSpots ?? 0} of{" "}
                {(sangha?.memberships?.length ?? 0) + (sangha?.openSpots ?? 0)} spots available
            </Button>

            <CSDialog
                onClose={() => setIsModalOpen(false)}
                open={isModalOpen}
                closeButtonSize="large-thin"
                transitionDuration={700}
                fullScreen={true}
            >
                <div data-testid="spotsAvailableModal">
                    <SpotsAvailableMemberList {...props} />
                </div>
            </CSDialog>
        </>
    );
};
